import { forwardRef } from 'react';
import { format } from 'date-fns';
import { Box, Flex, Image, Text } from '@chakra-ui/react';

import { ClockIcon } from '../icons';
import { styles, useCalendarSettings, useFilters } from '../../lib';

import type { BoxProps } from '@chakra-ui/react';
import type { IEvent } from '../../lib';

interface IEventGridTileProps extends Omit<BoxProps, 'children'> {
	event: IEvent;
	backgroundColor: string;
	textColor: string;
	isSameDayEvent: boolean;
	ignoreDirection: boolean;
	continuesPrior?: boolean;
}

export const EventGridTile = forwardRef<HTMLDivElement, IEventGridTileProps>(
	({ event, backgroundColor, textColor, isSameDayEvent, ignoreDirection, continuesPrior, ...rest }, ref) => {
		const { settings } = useCalendarSettings();
		const { currentView } = useFilters();
		const { iconStyle, lineClampSx } = styles.event;
		const { enableFeaturedImage, formats } = settings;
		const { monthFormat, timeFormat } = formats;

		return (
			<Box
				ref={ref}
				backgroundColor={backgroundColor}
				borderRadius='lg'
				boxSizing='border-box'
				color='white'
				cursor='pointer'
				height='inherit'
				lineHeight={1.2}
				whiteSpace='normal'
				{...rest}
			>
				<Flex
					flexDirection={(isSameDayEvent && currentView !== 'agenda') || ignoreDirection ? 'column' : 'row'}
					alignItems='center'
					overflow='hidden'
				>
					{event.image && enableFeaturedImage && !continuesPrior && (
						<Image margin='0px !important' src={event.image} maxW='150px' />
					)}
					<Box padding='10px'>
						<Text
							mb='0.5rem !important'
							fontSize='.9rem'
							fontWeight={600}
							sx={lineClampSx}
							color={textColor}
						>
							{event.title}
						</Text>
						<Text
							alignItems='baseline'
							color={textColor}
							display='flex'
							fontSize='.8rem'
							fontWeight={500}
							m={0}
						>
							<ClockIcon size={12} fill={textColor} style={iconStyle} />
							{format(event.start, isSameDayEvent ? timeFormat : `${monthFormat} ${timeFormat}`)} -{' '}
							{format(event.end, isSameDayEvent ? timeFormat : `${monthFormat} ${timeFormat}`)}
						</Text>
					</Box>
				</Flex>
			</Box>
		);
	}
);

EventGridTile.displayName = 'EventGridTile';
